import 'bootstrap-icons/font/bootstrap-icons.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'react-toastify/dist/ReactToastify.css';
import './App.scss';

import { useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Logtail } from "@logtail/browser";
import { GlobalTourProvider } from 'components/GlobalTourContext/GlobalTourContext';
import { useConfigRepo } from 'customHooks/useConfigRepo';
import { useProjectRepo } from 'customHooks/useProjectRepo';
import { useTokenExpiredHandler } from 'customHooks/useTokenExpiredHandler';
import guidesCollection from 'data/intros';
import { OnActiveProjectSubscriptionChangedFunction } from 'functions/functionDeclarations';
import { ErrorResponse } from 'models/ErrorResponse';
import { FeatureLimitation } from 'models/FeatureLimitation';
import { LogLevel } from 'models/LogLevel';
import ReactGA from "react-ga4";
import { RouteObject, useRoutes } from 'react-router-dom';
import { toast, ToastContainer, TypeOptions } from 'react-toastify';

import { AppContext, AppContextModel, NotificationType } from './components/AppContext/AppContext';
import Header from './components/Header/Header';
import Login from './components/Login/Login';
import MainContent from './components/MainContent/MainContent';
import modules from './modules';

ReactGA.initialize("G-PSS9G0ZWQJ");

function App() {
  const { getAccessTokenSilently, user } = useAuth0();
  const logtail = new Logtail("oy3DxCwewH1FWgE2VNCHas6y");
  const { configRepo } = useConfigRepo();
  const sendLog = (message: string, logLevel: LogLevel, context?: object) => {
    try {
      const data: any = { ...context };
      data.userId = user?.sub;
      // eslint-disable-next-line no-console
      console.log(message, logLevel, data);
      logtail.log(message, logLevel, data);
    } finally {
      logtail.flush();
    }
  };

  const notify = (text: string, type: NotificationType) => {
    toast(text, {
      position: 'top-center',
      // icon: true,
      type: type as TypeOptions,
    });
  };

  const onTokenExpired = useTokenExpiredHandler();
  const onActiveProjectSubscriptionChanged: OnActiveProjectSubscriptionChangedFunction = (featureLimitations: FeatureLimitation[], selectedCompanyId: number, errorMessage?: ErrorResponse) => {
    if (errorMessage) {
      let message;
      switch (errorMessage.errorCodeNumber) {
        case 401:
          message = 'Could not find subscription tied to selected project\'s company.  Please sign up for subscription using email address tied to company. Free tier limits have been applied.';
          sendLog(`failed to find company id ${selectedCompanyId} in subscription service.`, LogLevel.error);
          break;
        case 402:
          message = 'Could not find subscription tied to selected project\'s company.  Please sign up for subscription using email address tied to company. Free tier limits have been applied.';
          sendLog(`failed to find subscription tied to company id ${selectedCompanyId}.`, LogLevel.error);
          break;
        case 403:
          message = 'Your subscription has expired. Please purchase a new subscription for the current project\'s company. Free tier limits have been applied.';
          sendLog(`failed to find subscription tied to company id ${selectedCompanyId}.`, LogLevel.error);
          break;
        default:
          message = 'Failed to obtain subscription information for selected project\'s company. Free tier limits have been applied.';
          sendLog(`unknown error attempting to find subscriptino for company id ${selectedCompanyId}.`, LogLevel.error);
          break;
      }
      notify(message, NotificationType.Error);
      return;
    }

    sendLog(`feature limits for selected company id: ${selectedCompanyId} are ${JSON.stringify(featureLimitations)}`, LogLevel.info);
  };

  const { projects, selectedProjectId, setSelectedProjectIdAsync } = useProjectRepo(
    configRepo.SCHEDULE_API_BASE_URI,
    sendLog,
    configRepo.AUTH_API_BASE_URI,
    onTokenExpired,
    onActiveProjectSubscriptionChanged
  );
  const [isProjectSelectable, setIsProjectSelectable] = useState<boolean>(true);

  const appContext: AppContextModel = useMemo(
    () => ({
      getAccessTokenSilently,
      configRepo,
      projects,
      selectedProjectId,
      setSelectedProjectIdAsync,
      isProjectSelectable,
      setIsProjectSelectable,
      notify,
      sendLog
    }),
    [
      getAccessTokenSilently,
      configRepo,
      projects,
      selectedProjectId,
      setSelectedProjectIdAsync,
      isProjectSelectable,
      setIsProjectSelectable,
    ],
  );

  const defaultRoutes = [
    { path: '/', key: 'Login', element: <Login targetUrl="/account/verify" /> },
    {
      path: '*',
      key: 'NotFound',
      element: (
        <main style={{ padding: '1rem' }}>
          <p>There&#39;s nothing here!</p>
        </main>
      ),
    },
  ];
  const routes: RouteObject[] = modules.map((m) => m.routeProps);
  routes.push(...defaultRoutes);
  const element = useRoutes(routes);

  // This causes an unintended behavior.  If a user has an existing project, then creates a new project
  // SetupWizard calls `setSelectedProjectId(updatedEntity!.id as number)`. Then navigates to    navigate('/account', { replace: true });`
  // This then triggers this code to run and projects haven't been updated yet, then causing the default projectId to set
  // There could still be an open issue here with multiple test accounts and different project access
  // useEffect(() => {
  //   // fallback: if current selected project id is a project the user doesn't have access to then just select first project from list
  //   if (isAuthenticated && projects.length > 0 && selectedProjectId) {
  //     const currentProject = projects.find((p) => p.id === selectedProjectId);
  //     if (!currentProject) {
  //       setSelectedProjectId(projects[0].id);
  //     }
  //   }
  // }, [isAuthenticated, projects, selectedProjectId]);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    let Tawk_API: any;
    (window as any).Tawk_API = Tawk_API || {};
    (window as any).Tawk_LoadStart = new Date();
    const s1 = document.createElement("script");
    s1.async = true;
    s1.src = 'https://embed.tawk.to/668313cf9d7f358570d5eddd/1i1o0ap58';
    s1.charset = 'UTF-8';
    s1.setAttribute('crossorigin', '*');
    document.body.appendChild(s1);
  }, []);

  return (
    <div className="App">

      <AppContext.Provider value={appContext}>
        <GlobalTourProvider guidesCollection={guidesCollection}>
          <Header routeProps={modules.filter((m) => !m.isHidden)} />
          <MainContent modules={modules}>{element}</MainContent>
          <ToastContainer autoClose={3000} />
        </GlobalTourProvider>
      </AppContext.Provider>

    </div>
  );
}

export default App;
